import React, { useEffect, useState, useCallback } from "react";
import {
  Card,
  Typography,
  Button,
  Table,
  Tag,
  Tooltip,
  message,
  Space,
  Spin,
  Empty,
} from "antd";
import {
  CopyOutlined,
  GiftOutlined,
  LinkOutlined,
  ReloadOutlined,
  UserOutlined,
} from "@ant-design/icons";
import { API_CONST } from "../../../const";

const { Title, Text, Paragraph } = Typography;

const Referrals = () => {
  const [loadingCode, setLoadingCode] = useState(true);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [referralCode, setReferralCode] = useState(null);
  const [referralUrl, setReferralUrl] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchCode = useCallback(async () => {
    setLoadingCode(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(API_CONST.REFERRAL_MY_CODE, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setReferralCode(data.referralCode);
        setReferralUrl(
          data.referralCode
            ? `${window.location.origin}/signup?ref=${data.referralCode}`
            : null
        );
      }
    } catch {
      message.error("Failed to load referral code.");
    } finally {
      setLoadingCode(false);
    }
  }, []);

  const fetchCustomers = useCallback(async (currentPage = 1, currentPageSize = pageSize) => {
    setLoadingCustomers(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await fetch(
        `${API_CONST.REFERRAL_REFERRED_CUSTOMERS}?page=${currentPage}&pageSize=${currentPageSize}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.ok) {
        const data = await res.json();
        setCustomers(data.customers || []);
        setTotal(data.total || 0);
      }
    } catch {
      message.error("Failed to load referred customers.");
    } finally {
      setLoadingCustomers(false);
    }
  }, []);

  useEffect(() => {
    fetchCode();
    fetchCustomers(1);
  }, [fetchCode, fetchCustomers]);

  const handleCopyCode = () => {
    if (referralCode) {
      navigator.clipboard.writeText(referralCode);
      message.success("Referral code copied!");
    }
  };

  const handleCopyLink = () => {
    if (referralUrl) {
      navigator.clipboard.writeText(referralUrl);
      message.success("Referral link copied!");
    }
  };

  const columns = [
    {
      title: "Customer Organisation",
      dataIndex: "customerOrganization",
      key: "customerOrganization",
      render: (v) => <Text strong>{v}</Text>,
    },
    {
      title: "Contact Name",
      dataIndex: "contactName",
      key: "contactName",
    },
    {
      title: "Contact Email",
      dataIndex: "contactEmail",
      key: "contactEmail",
    },
    {
      title: "Date Joined",
      dataIndex: "dateJoined",
      key: "dateJoined",
      render: (v) => (v ? new Date(v).toLocaleDateString("en-IN") : "—"),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => {
        const map = { active: "success", pending_invite: "warning", declined: "error" };
        return <Tag color={map[status] || "default"}>{status?.replace("_", " ").toUpperCase()}</Tag>;
      },
    },
  ];

  return (
    <div style={{ maxWidth: 960, margin: "0 auto" }}>
      <div style={{ marginBottom: 24 }}>
        <Title level={3} style={{ color: "#014c75", margin: 0 }}>
          <GiftOutlined style={{ marginRight: 8, color: "#00d9a9" }} />
          Referrals
        </Title>
        <Paragraph style={{ color: "#666", marginTop: 4 }}>
          Share your referral link with prospective customers. When they sign up using your link, they'll
          be automatically linked to your account as a client.
        </Paragraph>
      </div>

      <Card
        style={{ marginBottom: 24, borderRadius: 8, border: "1px solid #e8f4fd", background: "#f0f9ff" }}
        bodyStyle={{ padding: 24 }}
      >
        {loadingCode ? (
          <Spin />
        ) : referralCode ? (
          <Space direction="vertical" size={16} style={{ width: "100%" }}>
            <div>
              <Text type="secondary" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
                Your Referral Code
              </Text>
              <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
                <Text
                  strong
                  style={{
                    fontSize: 28,
                    letterSpacing: 4,
                    color: "#014c75",
                    fontFamily: "monospace",
                  }}
                >
                  {referralCode}
                </Text>
                <Tooltip title="Copy code">
                  <Button icon={<CopyOutlined />} onClick={handleCopyCode} size="small">
                    Copy Code
                  </Button>
                </Tooltip>
              </div>
            </div>

            {referralUrl && (
              <div>
                <Text type="secondary" style={{ fontSize: 12, textTransform: "uppercase", letterSpacing: 1 }}>
                  Referral Link
                </Text>
                <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 4 }}>
                  <Text
                    style={{
                      fontFamily: "monospace",
                      fontSize: 13,
                      color: "#1890ff",
                      wordBreak: "break-all",
                    }}
                  >
                    {referralUrl}
                  </Text>
                  <Tooltip title="Copy link">
                    <Button
                      type="primary"
                      icon={<LinkOutlined />}
                      onClick={handleCopyLink}
                      style={{ background: "#00d9a9", borderColor: "#00d9a9", flexShrink: 0 }}
                    >
                      Copy Link
                    </Button>
                  </Tooltip>
                </div>
              </div>
            )}

            <Text type="secondary" style={{ fontSize: 12 }}>
              Share this link on LinkedIn, email, or wherever your prospects are. New customers who sign up
              using this link will automatically appear in your{" "}
              <Text strong>My Clients</Text> list.
            </Text>
          </Space>
        ) : (
          <Empty description="Referral code not available. Please contact support." />
        )}
      </Card>

      <Card
        title={
          <Space>
            <UserOutlined />
            <span>Referred Customers</span>
            <Tag color="blue">{total}</Tag>
          </Space>
        }
        extra={
          <Button
            icon={<ReloadOutlined />}
            onClick={() => fetchCustomers(page)}
            loading={loadingCustomers}
          >
            Refresh
          </Button>
        }
        style={{ borderRadius: 8 }}
      >
        <Table
          columns={columns}
          dataSource={customers}
          rowKey="customerOrganization"
          loading={loadingCustomers}
          pagination={{
            current: page,
            pageSize,
            total,
            onChange: (p) => {
              setPage(p);
              fetchCustomers(p, pageSize);
            },
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onShowSizeChange: (current, size) => {
              setPage(1);
              setPageSize(size);
              fetchCustomers(1, size);
            },
            showTotal: (t, range) => `${range[0]}-${range[1]} of ${t} rows`,
          }}
          locale={{ emptyText: "No referred customers yet. Share your referral link to get started!" }}
        />
      </Card>
    </div>
  );
};

export default Referrals;
